import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  TrendingUp,
  TrendingDown,
  DollarSign,
  Clock,
  Users,
  MessageSquare,
  CheckCircle,
  Minus
} from 'lucide-react';
import type { AnalyticsMetrics } from '@/types/ai-admin';

interface MetricCardsProps {
  analytics: AnalyticsMetrics;
}

export default function MetricCards({ analytics }: MetricCardsProps) {
  // Calculate trends (mock - in real app would compare to previous period)
  const queryTrend = 12.5; // % increase
  const costTrend = -8.3; // % decrease (good!)
  const timeTrend = -5.2; // % decrease (good!)

  const metrics = [
    {
      title: 'Total Queries',
      value: analytics.summary.totalQueries.toLocaleString(),
      icon: MessageSquare,
      trend: queryTrend,
      trendLabel: `${Math.abs(queryTrend)}%`,
      description: `${analytics.summary.avgQueriesPerUser.toFixed(1)} per user`,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100'
    },
    {
      title: 'Total Cost',
      value: `$${analytics.summary.totalCost.toFixed(2)}`,
      icon: DollarSign,
      trend: costTrend,
      trendLabel: `${Math.abs(costTrend)}%`,
      description: `$${(analytics.summary.totalCost / analytics.summary.totalQueries).toFixed(4)}/query`,
      color: 'text-green-600',
      bgColor: 'bg-green-100'
    },
    {
      title: 'Avg Response Time',
      value: `${(analytics.summary.avgResponseTime / 1000).toFixed(1)}s`,
      icon: Clock,
      trend: timeTrend,
      trendLabel: `${Math.abs(timeTrend)}%`,
      description: 'P95: 2.3s',
      color: 'text-purple-600',
      bgColor: 'bg-purple-100'
    },
    {
      title: 'Success Rate',
      value: `${analytics.summary.successRate.toFixed(1)}%`,
      icon: CheckCircle,
      trend: 0,
      trendLabel: 'Stable',
      description: `${analytics.errorAnalysis.totalErrors} errors`,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100'
    },
    {
      title: 'Unique Users',
      value: analytics.summary.uniqueUsers.toString(),
      icon: Users,
      trend: 15.0,
      trendLabel: '+3 users',
      description: 'Active this period',
      color: 'text-orange-600',
      bgColor: 'bg-orange-100'
    }
  ];

  const getTrendIcon = (trend: number) => {
    if (trend > 0) {
return <TrendingUp className="h-3 w-3" />;
}
    if (trend < 0) {
return <TrendingDown className="h-3 w-3" />;
}
    return <Minus className="h-3 w-3" />;
  };

  const getTrendColor = (trend: number, isPositiveGood: boolean = true) => {
    if (trend === 0) {
return 'text-muted-foreground';
}
    if (isPositiveGood) {
      return trend > 0 ? 'text-green-600' : 'text-red-600';
    } else {
      return trend < 0 ? 'text-green-600' : 'text-red-600';
    }
  };

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
      {metrics.map((metric, index) => {
        const Icon = metric.icon;
        // For cost and response time, negative trend is good
        const isPositiveGood = !['Total Cost', 'Avg Response Time'].includes(metric.title);

        return (
          <Card key={index}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{metric.title}</CardTitle>
              <div className={`p-2 rounded-lg ${metric.bgColor}`}>
                <Icon className={`h-4 w-4 ${metric.color}`} />
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{metric.value}</div>
              <div className="flex items-center space-x-2 mt-1">
                <Badge
                  variant="outline"
                  className={`text-xs ${getTrendColor(metric.trend, isPositiveGood)}`}
                >
                  {getTrendIcon(metric.trend)}
                  <span className="ml-1">{metric.trendLabel}</span>
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                {metric.description}
              </p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}